import React, { useState, useEffect } from "react";

import { t } from "i18next";
import { isEmpty } from "lodash";

import BaseAlertModal from "../Modals/BaseAlertModal";
import ValidationError from "../Shared/ValidationError";
import LicensePlansAPI from "../../API/licensePlans.api";
import OrganizationsAPI from "../../API/organizations.api";
import OrganizationLicenseAPI from "../../API/organizationLicense.api";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { validate } from "../../utils/ValidationHelpers";
import SharedSchema from "../../schemas/shared.schema";

const SubscriptionPlansCards = ({ currentUser, setCurrentUser }) => {
    const [plans, setPlans] = useState([]);
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState("");
    const [plansMap, setPlansMap] = useState({});
    const [choosenCurrency, setChoosenCurrency] = useState({});

    //Complete data modal states
    const [dataModal, setDataModal] = useState(false);
    const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
    const [isDataModalValid, setIsDataModalValid] = useState(false);
    const [userOrganizationEmail, setUserOrganizationEmail] = useState("");
    const [error, setError] = useState({});
    const [isDataModalSubmitted, setIsDataModalSubmitted] = useState(false);

    useEffect(() => {
        getPlans();

        return () => {
            setPlansMap({});
            setPlans([]);
        };
    }, []);

    useEffect(() => {
        if (!dataModal) resetDataModal();
    }, [dataModal]);

    useEffect(() => {
        validate(
            { email: userOrganizationEmail },
            SharedSchema.EmailSchema,
            setIsDataModalValid,
            setError
        );
    }, [userOrganizationEmail]);

    const resetDataModal = () => {
        setIsUpdatingEmail(false);
        setIsDataModalValid(false);
        setUserOrganizationEmail("");
        setIsDataModalSubmitted(false);
    };

    const handleSubscription = async (plan, email) => {
        if (!currentUser.organization?.email && !email) {
            setDataModal(true);
            return;
        }

        setIsSubscribing(true);
        const checkoutUrl =
            await OrganizationLicenseAPI.createLicenseCheckoutSession(
                plansMap[plan?.id || selectedPlan?.id][
                    choosenCurrency[plan.id] ||
                        currentUser?.organization.defaultCurrencyId
                ].id
            );

        if (checkoutUrl) {
            window.open(checkoutUrl, "_self");
            LogCapture.info(
                `User ${
                    currentUser.organization?.email || email
                } successfully subscribed to plan ${
                    plan?.name || selectedPlan?.name
                }`
            );
        } else {
            LogCapture.error(
                `User ${
                    currentUser.organization?.email || email
                } failed to subscribe to plan ${
                    plan?.name || selectedPlan?.name
                }`
            );
        }

        setIsSubscribing(false);
    };

    const updateUserOrganizationEmail = async () => {
        setIsDataModalSubmitted(true);

        if (!isDataModalValid) {
            return Promise.reject();
        }

        setIsUpdatingEmail(true);
        const result = await OrganizationsAPI.updateOrganizationInfo({
            id: currentUser.organization.id,
            email: userOrganizationEmail,
        });

        if (result.modified) {
            setCurrentUser((prev) => ({
                ...prev,
                organization: {
                    ...prev.organization,
                    email: userOrganizationEmail,
                    image: prev.organization.image,
                },
            }));
            handleSubscription(null, userOrganizationEmail);
        }
        setIsUpdatingEmail(false);
    };

    const getPlans = async () => {
        const result = await LicensePlansAPI.getLicensePlans();
        setPlansMap(formatPlansMap(result));
        setPlans(result);
    };

    const formatPlansMap = (plans) => {
        const dict = {};

        plans.forEach((plan) => {
            const dictPrices = {};

            plan.prices.forEach((price) => {
                dictPrices[price.currency.value] = price;
            });

            dict[plan.id] = dictPrices;
        });

        return dict;
    };

    const renderPoints = (points) => {
        return points?.map((point, index) => {
            return (
                <li key={index} className="w-full">
                    <span className="w-full">{point}</span>
                </li>
            );
        });
    };

    const getMultiCurrencyTabs = (plan, planIndex) => {
        const getIsTabActive = (price) => {
            return (
                choosenCurrency[plan.id] === price.currency.value ||
                (!choosenCurrency[plan.id] &&
                    price.currency.value ===
                        currentUser?.organization?.defaultCurrencyId)
            );
        };

        const getAvailableCurrencies = () =>
            plan?.prices?.map((price, index) => (
                <li
                    key={`currency-tabs-${planIndex}-${index}`}
                    className={`text-sm tab-lined-item ${
                        getIsTabActive(price) ? "active" : ""
                    }`}
                    onClick={() =>
                        setChoosenCurrency((prev) => ({
                            ...prev,
                            [plan.id]: price.currency.value,
                        }))
                    }
                >
                    {price.currency.abbreviation}
                </li>
            ));

        return (
            currentUser?.organization?.isDefaultCurrencyEditable &&
            plan?.prices.length > 1 && (
                <ul className="tabs-lined-wrapper flex border-gray">
                    {getAvailableCurrencies()}
                </ul>
            )
        );
    };

    const renderPlans = () => {
        return plans?.map((plan, index) => (
            <div
                key={index}
                className="border border-secondary-color-more-lighter-blue rounded w-11/12 lg:w-72 xl:w-80 bg-secondary-color-lighter-white h-full"
            >
                <div className="flex flex-col h-full items-center">
                    <h2 className="h2 p-3 border-b-2 w-full text-center bg-primary-color-dark-blue text-white font-bold">
                        {plan.name}
                    </h2>
                    {getMultiCurrencyTabs(plan, index)}
                    <ul className="self-left list-disc px-12 space-y-2 colored-list my-4">
                        {renderPoints(plan.description)}
                    </ul>
                    <button
                        className={`default-orange-outline-btn uppercase my-6 mt-auto w-2/3 ${
                            isSubscribing ? "disabled" : ""
                        }`}
                        disabled={isSubscribing}
                        onClick={() => {
                            setSelectedPlan(plan);
                            plan.id && handleSubscription(plan);
                        }}
                    >
                        {t("buy")}
                    </button>
                </div>
            </div>
        ));
    };

    const changeOrganizationEmail = (e) => {
        setUserOrganizationEmail(e.target.value);
    };

    return (
        <>
            {!isEmpty(plans) && (
                <div className="flex flex-col flex-wrap items-center justify-center h-full lg:flex-row gap-2 border-primary-color-orange">
                    {renderPlans()}
                </div>
            )}
            <BaseAlertModal
                isVisible={dataModal}
                setIsVisible={setDataModal}
                title={t("missingData")}
                isSubmitting={isUpdatingEmail}
                message={t("missingAffiliateEmail")}
                action={{
                    name: t("submit"),
                    submitAction: updateUserOrganizationEmail,
                    disabled: isDataModalSubmitted && !isDataModalValid,
                }}
                type="warning"
            >
                <div className="input-group mt-2">
                    <input
                        type="text"
                        placeholder={t("emailPlaceholder")}
                        className="input-element"
                        value={userOrganizationEmail}
                        onChange={changeOrganizationEmail}
                    />
                    {isDataModalSubmitted && !isDataModalValid && (
                        <ValidationError errorMessage={error.email} />
                    )}
                </div>
            </BaseAlertModal>
        </>
    );
};

export default SubscriptionPlansCards;
